import {ChangeDetectionStrategy, Component, effect, Injectable, input, output} from '@angular/core';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {MatButtonModule} from "@angular/material/button";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatTooltipModule} from "@angular/material/tooltip";
import {TranslateModule} from "@ngx-translate/core";
import {CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList} from "@angular/cdk/drag-drop";
import {GruppeId, MedlemsRolleId, MedlemsRolleModell, MedlemsRolleRessursClient, MedlemsRolleSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {MatIcon} from "@angular/material/icon";
import {KortHandlingerKomponent} from "web-ng2/delt/komponenter/kort/kort-handlinger/kort-handlinger";
import {håndterRedigerListeEndringer} from "felles-ng2/infrastruktur/angular/redigerListeSkjema";

export type RolleSkjema = ReturnType<typeof GruppeRolleRedigerListeKomponent.opprettSkjemarad>
export type RolleSkjemaVerdier = RolleSkjema['value']

@Component({
    selector: 'gruppe-rolle-rediger-liste',
    templateUrl: './gruppe-rolle-rediger-liste.html',
    styleUrls: ['./gruppe-rolle-rediger-liste.scss'],
    imports: [
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatTooltipModule,
        CdkDropList,
        CdkDrag,
        CdkDragHandle,
        TranslateModule,
        StandardValideringsfeilPipe,
        MatIcon,
        KortHandlingerKomponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GruppeRolleRedigerListeKomponent {

    /** Kalles når bruker har lagret eller avbryter, returnerer oppdaterte roller ved lagret, null ved avbrutt */
    vedFerdig = output<MedlemsRolleModell[] | null>();

    roller = input.required<MedlemsRolleModell[]>();
    gruppeId = input.required<GruppeId>();

    lagrer = asynkArbeidPågårSignal();

    skjema = new FormArray<RolleSkjema>([]);

    static opprettSkjemarad(verdier = {id: null as MedlemsRolleId | null, tittel: '', ny: true, kanIkkeSlettes: false}) {
        return new FormGroup({
            id: new FormControl<MedlemsRolleId | null>(verdier.id),
            tittel: new FormControl<string>(verdier.tittel, {nonNullable: true, validators: FeltValidatorer.litenStreng}),
            slettet: new FormControl<boolean>(false, {nonNullable: true}),
            kanIkkeSlettes: new FormControl<boolean>(verdier.kanIkkeSlettes, {nonNullable: true}),
            ny: new FormControl<boolean>(verdier.ny, {nonNullable: true})
        });
    }

    constructor(private avhengigheter: GruppeRolleRedigerListeKomponentAvhengigheter) {
        this.skjema.valueChanges.pipe(takeUntilDestroyed())
          .subscribe(() => håndterRedigerListeEndringer(this.skjema, GruppeRolleRedigerListeKomponent.opprettSkjemarad));

        effect(() => {
            this.skjema.clear();
            this.roller().forEach(rolle =>
              this.skjema.push(GruppeRolleRedigerListeKomponent.opprettSkjemarad({...rolle, ny: false}), {emitEvent: false}));
            //sørger for at det blir lagt til en tom 'nytt innslag' rad
            håndterRedigerListeEndringer(this.skjema, GruppeRolleRedigerListeKomponent.opprettSkjemarad);
        });
    }

    async lagre() {
        const oppdaterteRoller = await this.lagrer.utfør(() =>
          this.avhengigheter.oppdaterRoller(this.gruppeId(), this.somSpesifikasjon()));
        this.vedFerdig.emit(oppdaterteRoller);
    }

    avbryt() {
        this.vedFerdig.emit(null);
    }

    slett(indeks: number) {
        const rad = this.skjema.at(indeks);
        if (rad.value.kanIkkeSlettes) return;

        if (rad.value.ny) {
            this.skjema.removeAt(indeks);
        } else {
            rad.disable();
            rad.controls.slettet.setValue(true);
        }
    }

    angreSlett(indeks: number) {
        const rad = this.skjema.at(indeks);
        rad.enable();
        rad.controls.slettet.setValue(false);
    }

    flyttingTillatFn() {
        return (_indeks: number, _flyttetRad: CdkDrag<number>) => {
            return _indeks < this.skjema.controls.length - 1;
        };
    }

    radFlyttet(event: CdkDragDrop<any>) {
        const rad = this.skjema.at(event.previousIndex);
        this.skjema.removeAt(event.previousIndex);
        this.skjema.insert(event.currentIndex, rad);
    }

    private somSpesifikasjon(): MedlemsRolleSpesifikasjon[] {
        return this.skjema.controls
          .filter(rad => !rad.value.slettet && !erTomEllerNil(rad.value.tittel))
          .map(rad => ({
              id: rad.value.id as unknown as MedlemsRolleId,
              tittel: rad.value.tittel!
          }));
    }
}

@Injectable()
export class GruppeRolleRedigerListeKomponentAvhengigheter {

    constructor(private rolleRessurs: MedlemsRolleRessursClient) {
    }

    oppdaterRoller(gruppeId: GruppeId, roller: MedlemsRolleSpesifikasjon[]): Promise<MedlemsRolleModell[]> {
        return this.rolleRessurs.oppdaterRoller(gruppeId, roller).somPromise();
    }
}
